// The shell's knobs on the drivers, and the one moment they stop for a decision.
//
// The drivers live in the engine (`game.drivers`, PLAN-CLIENT-SPLIT day 7).
// This is the adapter: every render pushes the shell's options through
// `configure` — the engine keeps what it last had, so a field that did not
// change costs nothing — and the branch a cleared front is offering arrives
// through `Events.branch`.
//
// **Nothing here is a dependency list.** The hooks this replaced listed
// sixteen things in one effect and stalled whenever one of them was not
// stable; `configure` takes a plain object and the engine decides.

import { useEffect, useState } from "react";

import type { Branch, DriverOptions, Game } from "src/api";

/** A front waiting on the player, and the way to answer it. */
export interface SoloBranch {
  branch: Branch | null;
  /**
   * Land one of the offers, by index. **Called with nothing it lands the
   * machine's choice**: anything that can dismiss the panel has to leave a
   * camp behind it.
   */
  pick(index?: number): void;
}

export function useOpponentDriver(game: Game, options: DriverOptions): SoloBranch {
  const {
    enabled,
    playerSide,
    level,
    pace,
    tuning,
    profiles,
    paused,
    step,
    branching,
    lead,
    fast,
    clockHealth,
    waveDelay
  } = options;
  // Field by field, so a shell that rebuilds the options object every render
  // configures only when a value moved.
  useEffect(() => {
    game.drivers.configure({
      enabled,
      playerSide,
      level,
      pace,
      tuning,
      profiles,
      paused,
      step,
      branching,
      lead,
      fast,
      clockHealth,
      waveDelay
    });
  }, [
    game,
    enabled,
    playerSide,
    level,
    pace,
    tuning,
    profiles,
    paused,
    step,
    branching,
    lead,
    fast,
    clockHealth,
    waveDelay
  ]);
  // Off on the way out, so a shell that unmounts leaves no opponent playing an
  // empty room — the engine's drivers outlive the view.
  useEffect(() => () => game.drivers.configure({ enabled: false }), [game]);

  const [branch, setBranch] = useState<Branch | null>(() => game.drivers.branch());
  useEffect(() => game.on("branch", setBranch), [game]);

  return { branch, pick: (index) => game.drivers.pick(index) };
}
